import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { TextBlocksHeaderOptions, createDefaultValues } from './common/text-blocks-constants';
import SwitchBar from '../../components/switch-bar';

const AI_PROMPT_KEYS = [
  'SHARED_AI_PROMPT',
  'INTENT_EVALUATION_PROMPT',
  'CONVERSATION_STARTER_PROMPT',
  'SEARCH_RESPONSE_PROMPT',
  'CLARIFY_INTENT_PROMPT',
  'CRISIS_EVALUATION_PROMPT',
  'SCORE_CONTENT_PROMPT',
  'SCORE_PEOPLE_PROMPT',
  'SCORE_RESOURCES_PROMPT',
  'SCORE_PROVIDER_PLANS_PROMPT',
  'CHAT_TAGS_PROMPT',
  'CHECKIN_QUERY_PROMPT',
  'LISTENER_AUDIO_TITLE_PROMPT',
  'LISTENER_AUDIO_TAG_PROMPT',
  'LISTENER_AUDIO_SUMMARY_PROMPT',
] as const;

type AIPromptKey = (typeof AI_PROMPT_KEYS)[number];

export const AIPrompts = () => {
  const { data: appSettings, isLoading } = useSettings('email');
  const updateSettings = useUpdateSettings();

  const defaultAIPrompts = useMemo(() => createDefaultValues(AI_PROMPT_KEYS, appSettings), [appSettings]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues: defaultAIPrompts,
  });

  const cancelRef = useRef() as React.MutableRefObject<HTMLButtonElement>;

  useEffect(() => {
    if (defaultAIPrompts) {
      reset(defaultAIPrompts);
    }
  }, [defaultAIPrompts, reset]);

  const onSubmit = (data: any) => {
    const payload = {
      data: {
        message: appSettings?.message,
        system: { ...appSettings?.system, ...data },
      },
    };

    updateSettings.mutate(payload, {
      onSuccess: () => {
        toast.success('AI Prompts updated successfully!');
        cancelRef.current.classList.remove('bg-red');
        cancelRef.current.classList.add('bg-gray-background-dark');
      },
      onError: () => {
        toast.error('Failed to update AI Prompts. Please try again.');
      },
    });
  };

  const handleCancel = () => {
    reset(defaultAIPrompts);
    cancelRef.current.classList.remove('bg-red');
    cancelRef.current.classList.add('bg-gray-background-dark');
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>, key: AIPromptKey) => {
    setValue(key, e.target.value);
    cancelRef.current.classList.remove('bg-gray-background-dark');
    cancelRef.current.classList.add('bg-red');
  };

  return (
    <div>
      <Header heading={'Shared AI Prompt Settings'} />
      <SwitchBar heading={TextBlocksHeaderOptions} position={4} />
      <div className="max-window-height overflow-y-auto pb-10">
        <div className="w-full px-7">
          {isLoading && <Spinner />}
          {!isLoading &&
            !appSettings?.system?.SHARED_AI_PROMPT &&
            !appSettings?.system?.INTENT_EVALUATION_PROMPT &&
            !appSettings?.system?.CONVERSATION_STARTER_PROMPT &&
            !appSettings?.system?.SEARCH_RESPONSE_PROMPT && (
              <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-4">
                <p className="text-yellow-800">
                  No AI prompts are currently configured. Use the textareas below to add them.
                </p>
              </div>
            )}
          <div className="mt-6">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-8">
                <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">
                  Chat Graph Workflow Prompts
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                  These prompts control our Persona Chat Graph. They determine the general tone of the AI, as well as
                  providing context and instructions for individual steps in the chat graph.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="SHARED_AI_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Shared AI Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Base prompt used across all Peersonas. Sets the overall tone and context for the AI system.
                </p>
                <textarea
                  id="SHARED_AI_PROMPT"
                  {...register('SHARED_AI_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'SHARED_AI_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the shared AI prompt..."
                  data-testid="shared-ai-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="INTENT_EVALUATION_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Intent Evaluation Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Determines user intent using conversation history and current message. Classifies as "search"
                  (information), "vent" (emotional support), or "clarify" (ambiguous).
                </p>
                <textarea
                  id="INTENT_EVALUATION_PROMPT"
                  {...register('INTENT_EVALUATION_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'INTENT_EVALUATION_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the intent evaluation prompt..."
                  data-testid="intent-evaluation-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="CLARIFY_INTENT_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Clarify Intent Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Generates a clarifying question when user intent is ambiguous. Helps determine whether they want
                  information or emotional support.
                </p>
                <textarea
                  id="CLARIFY_INTENT_PROMPT"
                  {...register('CLARIFY_INTENT_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'CLARIFY_INTENT_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the clarify intent prompt..."
                  data-testid="clarify-intent-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="CONVERSATION_STARTER_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Conversation Starter Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Creates personalized greetings for returning users. Uses conversation history to provide a welcome
                  back message.
                </p>
                <textarea
                  id="CONVERSATION_STARTER_PROMPT"
                  {...register('CONVERSATION_STARTER_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'CONVERSATION_STARTER_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the conversation starter prompt..."
                  data-testid="conversation-starter-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="SEARCH_RESPONSE_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Search Response Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  When the AI performs a search on behalf of the user, this prompt is used to generate a response that
                  is helpful and contextual.
                </p>
                <textarea
                  id="SEARCH_RESPONSE_PROMPT"
                  {...register('SEARCH_RESPONSE_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'SEARCH_RESPONSE_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the search response prompt..."
                  data-testid="search-response-prompt-textarea"
                />
              </div>

              {/*CHAT_TAGS_PROMPT*/}
              <div className="mb-4">
                <label htmlFor="CHAT_TAGS_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Chat Tags Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">Evaluates chat messages for topics discussed.</p>
                <textarea
                  id="CHAT_TAGS_PROMPT"
                  {...register('CHAT_TAGS_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'CHAT_TAGS_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the chat tags prompt..."
                  data-testid="chat-tags-prompt-textarea"
                />
              </div>

              {/*CHECKIN_QUERY_PROMPT*/}
              <div className="mb-4">
                <label htmlFor="CHECKIN_QUERY_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Check-in Query Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Evaluates relevance of resources against user's chat history.
                </p>
                <textarea
                  id="CHECKIN_QUERY_PROMPT"
                  {...register('CHECKIN_QUERY_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'CHECKIN_QUERY_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter check-in query prompt..."
                  data-testid="check-in-query-prompt-textarea"
                />
              </div>

              <div className="mb-8 mt-12">
                <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">
                  Simple Search Workflow Prompts
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                  These prompts control our Simple Search / Pathway Graph. They determine the steps the AI takes to find
                  relevant items for the user.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="CRISIS_EVALUATION_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Crisis Evaluation Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  This prompt is shared between the Persona Chat Graph and the Simple Search / Pathway Graph. It is used
                  to determine if the user is in a crisis and needs immediate help.
                </p>
                <textarea
                  id="CRISIS_EVALUATION_PROMPT"
                  {...register('CRISIS_EVALUATION_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'CRISIS_EVALUATION_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the crisis evaluation prompt..."
                  data-testid="crisis-evaluation-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="SCORE_CONTENT_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Score Content Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Evaluates relevance of content items (Listener Audio/Video) against user queries. We currently embed
                  our transcription and admin summary for scoring.
                </p>
                <textarea
                  id="SCORE_CONTENT_PROMPT"
                  {...register('SCORE_CONTENT_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'SCORE_CONTENT_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the score content prompt..."
                  data-testid="score-content-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="SCORE_PEOPLE_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Score People Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Evaluates relevance of people ("Peers") against user queries. We currently embed the Peer's name,
                  state, and about me for scoring.
                </p>
                <textarea
                  id="SCORE_PEOPLE_PROMPT"
                  {...register('SCORE_PEOPLE_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'SCORE_PEOPLE_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the score people prompt..."
                  data-testid="score-people-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="SCORE_RESOURCES_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Score Resources Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Evaluates relevance of resources ("Programs" and "Resources" ie Client and Global Resources) against
                  user queries. We currently embed the resource's name, description, and long description for scoring.
                </p>
                <textarea
                  id="SCORE_RESOURCES_PROMPT"
                  {...register('SCORE_RESOURCES_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'SCORE_RESOURCES_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the score resources prompt..."
                  data-testid="score-resources-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="SCORE_PROVIDER_PLANS_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Score Provider Plans Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Evaluates relevance of external provider plans against user queries. We currently embed the provider
                  plan's title, description, and common uses for scoring.
                </p>
                <textarea
                  id="SCORE_PROVIDER_PLANS_PROMPT"
                  {...register('SCORE_PROVIDER_PLANS_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'SCORE_PROVIDER_PLANS_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the score provider plans prompt..."
                  data-testid="score-provider-plans-prompt-textarea"
                />
              </div>

              <div className="mb-8 mt-10">
                <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">
                  Listener Audio Enrichment
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                  These prompts power automated listener audio metadata (AI titles, summaries, and topic tags). Titles
                  expect concise, third-person summaries; summaries capture the core emotional and practical concerns;
                  tag scoring guides the AI on matching transcripts to topic tags.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="LISTENER_AUDIO_TITLE_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Listener Audio Title Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  System prompt for generating short, descriptive AI titles from listener audio transcripts.
                </p>
                <textarea
                  id="LISTENER_AUDIO_TITLE_PROMPT"
                  {...register('LISTENER_AUDIO_TITLE_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'LISTENER_AUDIO_TITLE_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the listener audio title prompt..."
                  data-testid="listener-audio-title-prompt-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="LISTENER_AUDIO_TAG_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Listener Audio Tag Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  System prompt for scoring how well topic tags match listener audio transcripts.
                </p>
                <textarea
                  id="LISTENER_AUDIO_TAG_PROMPT"
                  {...register('LISTENER_AUDIO_TAG_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'LISTENER_AUDIO_TAG_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the listener audio tag prompt..."
                  data-testid="listener-audio-tag-prompt-textarea"
                />
              </div>

              <div className="mb-8">
                <label htmlFor="LISTENER_AUDIO_SUMMARY_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                  Listener Audio Summary Prompt
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  System prompt for generating concise, empathetic summaries of listener audio transcripts that capture
                  the core emotional and practical concerns.
                </p>
                <textarea
                  id="LISTENER_AUDIO_SUMMARY_PROMPT"
                  {...register('LISTENER_AUDIO_SUMMARY_PROMPT')}
                  onChange={(e) => handleTextareaChange(e, 'LISTENER_AUDIO_SUMMARY_PROMPT')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the listener audio summary prompt..."
                  data-testid="listener-audio-summary-prompt-textarea"
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="submit"
                  disabled={!isValid}
                  className="bg-bright-blue rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  data-testid="ai-prompts-save-button"
                >
                  {updateSettings.isLoading ? 'Saving...' : 'Save'}
                </button>

                <button
                  type="button"
                  ref={cancelRef}
                  onClick={handleCancel}
                  className="bg-gray-background-dark rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-gray-600"
                  data-testid="ai-prompts-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
